"use client";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './BotanicalHero.css';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const ROSE_IMG = 'https://cloud.sharemyimage.com/2026/08/20/Rose-Petals.png';
const HIBISCUS_IMG = 'https://cloud.sharemyimage.com/2026/08/20/Hibiscus-Flowers.png';
const PEA_IMG = 'https://cloud.sharemyimage.com/2026/08/20/Butterfly-Pea-Flowers.png';
const LOTUS_IMG = 'https://cloud.sharemyimage.com/2026/08/20/Dry-Blue-Lotus.png';

const flavors = [
  {
    id: 'rose',
    flavorName: 'Rose Petals',
    bgColor: '#F4A0B0',
    bgHeadingColor: '#E8899A',
    bgHeadingText: 'Rose',
    description: 'Darshana delivers the delicate essence of hand-picked rose petals, naturally dried to preserve their romantic fragrance and wellness properties from Sri Lanka.',
    image: ROSE_IMG,
    textColor: '#FFFFFF'
  },
  {
    id: 'hibiscus',
    flavorName: 'Hibiscus Bloom',
    bgColor: '#E11D48',
    bgHeadingColor: '#BE123C',
    bgHeadingText: 'Hibiscus',
    description: 'Experience the vibrant, antioxidant-rich power of naturally dried hibiscus flowers, delivering bold colour and refreshing tartness in every preparation.',
    image: HIBISCUS_IMG,
    textColor: '#FFFFFF'
  },
  {
    id: 'butterfly-pea',
    flavorName: 'Butterfly Pea',
    bgColor: '#3d29ba',
    bgHeadingColor: '#2d1f8a',
    bgHeadingText: 'Butterfly Pea',
    description: 'Discover the mesmerizing indigo hues of butterfly pea flowers, packed with natural antioxidants and creating stunning colour-changing botanical beverages.',
    image: PEA_IMG,
    textColor: '#FFFFFF'
  },
  {
    id: 'blue-lotus',
    flavorName: 'Blue Lotus',
    bgColor: '#5a30ba',
    bgHeadingColor: '#4625a0',
    bgHeadingText: 'Blue Lotus',
    description: 'Indulge in the sacred blue lotus, carefully preserved to maintain its calming botanical properties and exquisite visual appeal for premium wellness rituals.',
    image: LOTUS_IMG,
    textColor: '#FFFFFF'
  }
];

const layerZ = [10, 20, 30, 40];
const AUTO_INTERVAL = 5000;

function BotanicalHero() {
  const [activeIndex, setActiveIndex] = useState(0);
  const isTransitioning = useRef(false);
  const autoTimer = useRef(null);

  const active = flavors[activeIndex];

  const goTo = useCallback((index) => {
    if (isTransitioning.current) return;
    const clamped = ((index % flavors.length) + flavors.length) % flavors.length;
    isTransitioning.current = true;
    setActiveIndex(clamped);
    setTimeout(() => { isTransitioning.current = false; }, 1000);
  }, []);

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    autoTimer.current = setInterval(() => {
      setActiveIndex((prev) => {
        if (isTransitioning.current) return prev;
        isTransitioning.current = true;
        setTimeout(() => { isTransitioning.current = false; }, 1000);
        return (prev + 1) % flavors.length;
      });
    }, AUTO_INTERVAL);
    return () => clearInterval(autoTimer.current);
  }, []);

  const handleArrow = (dir) => {
    clearInterval(autoTimer.current);
    if (dir === 'next') goNext();
    else goPrev();
    autoTimer.current = setInterval(() => {
      setActiveIndex((prev) => {
        if (isTransitioning.current) return prev;
        isTransitioning.current = true;
        setTimeout(() => { isTransitioning.current = false; }, 1000);
        return (prev + 1) % flavors.length;
      });
    }, AUTO_INTERVAL);
  };

  return (
    <div
      className="dgh-hero-wrapper"
      style={{ backgroundColor: active.bgColor }}
    >
      <div className="dgh-hero-inner">
        {flavors.map((flavor, i) => {
          const isActive = i === activeIndex;
          const layerStyle = {
            position: 'absolute',
            inset: 0,
            zIndex: layerZ[i],
            transform: i <= activeIndex ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 1000ms cubic-bezier(0.16, 1, 0.3, 1)'
          };

          return (
            <div key={flavor.id} className="dgh-slide-slidein" style={layerStyle}>
              <div className="dgh-slide-container">
                <div className="dgh-slide-graphics">
                  <div
                    className={`dgh-bg-heading ${isActive ? 'dgh-active' : 'dgh-inactive'}`}
                    style={{ color: flavor.bgHeadingColor }}
                  >
                    {flavor.bgHeadingText}
                  </div>

                  {[1, 2, 3, 4, 5].map((s) => (
                    <div
                      key={s}
                      className={`dgh-slice dgh-slice-${s} ${isActive ? 'dgh-active' : 'dgh-inactive'}`}
                    >
                      <img src={flavor.image} alt="" referrerPolicy="no-referrer" />
                    </div>
                  ))}

                  <div className={`dgh-can-wrapper ${isActive ? 'dgh-active' : 'dgh-inactive'}`}>
                    <div className="dgh-can-inner">
                      <img src={flavor.image} alt={flavor.flavorName} referrerPolicy="no-referrer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <button
          className="dgh-arrow dgh-arrow-left"
          onClick={() => handleArrow('prev')}
          aria-label="Previous botanical"
        >
          <SafeIcon icon={FiChevronLeft} />
        </button>
        <button
          className="dgh-arrow dgh-arrow-right"
          onClick={() => handleArrow('next')}
          aria-label="Next botanical"
        >
          <SafeIcon icon={FiChevronRight} />
        </button>

        <div className="dgh-dots">
          {flavors.map((f, i) => (
            <button
              key={f.id}
              className={`dgh-dot ${i === activeIndex ? 'dgh-dot-active' : ''}`}
              onClick={() => handleArrow(i >= activeIndex ? 'next' : 'prev')}
              aria-label={`Go to ${f.flavorName}`}
            />
          ))}
        </div>

        <div className="dgh-sticky-footer">
          <div
            className="dgh-footer-desc"
            style={{
              color: active.textColor,
              WebkitTextStroke: `1.5px ${active.bgColor}`,
              textShadow: `
                -1px -1px 0 ${active.bgColor},
                 1px -1px 0 ${active.bgColor},
                -1px  1px 0 ${active.bgColor},
                 1px  1px 0 ${active.bgColor},
                 0   -2px 0 ${active.bgColor},
                 0    2px 0 ${active.bgColor},
                -2px  0   0 ${active.bgColor},
                 2px  0   0 ${active.bgColor}
              `
            }}
          >
            {active.description}
          </div>
          <span className="dgh-flavor-label">{active.flavorName}</span>
        </div>

        <div className="dgh-slide-counter">
          <span className="dgh-counter-current">{String(activeIndex + 1).padStart(2, '0')}</span>
          <span className="dgh-counter-sep">/</span>
          <span className="dgh-counter-total">{String(flavors.length).padStart(2, '0')}</span>
        </div>
      </div>
    </div>
  );
}

export default BotanicalHero;